import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Check, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { API } from '../config/api';

const benefits = [
  'Purchase any artwork from the gallery',
  'Access to full-resolution downloads',
  'Support authentic human artists',
  'Early access to new collections',
  'No hidden fees on purchases',
];

export default function Membership() {
  const navigate = useNavigate();
  const { isAuthenticated, isMember } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/payments/membership`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Subscription failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (isMember) {
    return (
      <div className="min-h-screen py-16 px-4 flex items-center justify-center">
        <div className="max-w-md text-center">
          <Crown className="mx-auto text-[#D4AF37] mb-6" size={48} />
          <h1 className="font-serif text-2xl text-[#F5F5F0] mb-4">You're a Member!</h1>
          <p className="text-[#A1A1AA] mb-8">
            You have full access to purchase any artwork in the gallery.
          </p>
          <Button onClick={() => navigate('/gallery')} className="btn-primary">
            Explore Gallery
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Become a</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0]">
            Collector Member
          </h1>
          <p className="mt-6 text-lg text-[#A1A1AA] max-w-lg mx-auto">
            Join our community of art collectors and gain access to purchase 
            authentic human-created artwork.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto">
          <div className="p-8 bg-[#121212] border border-[#D4AF37]/30 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="badge badge-gold">
                <Crown size={12} className="mr-1" />
                Collector
              </span>
            </div>
            
            <div className="text-center pt-4">
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-serif text-5xl text-[#F5F5F0]">$5</span>
                <span className="text-[#71717A]">/month</span>
              </div>
              <p className="text-[#71717A] text-sm mt-2">Cancel anytime</p>
            </div>

            <ul className="mt-8 space-y-4">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-3">
                  <Check className="text-[#D4AF37] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-[#A1A1AA]">{benefit}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={handleSubscribe}
              disabled={loading}
              className="btn-primary w-full mt-8 flex items-center justify-center gap-2"
              data-testid="subscribe-btn"
            >
              <CreditCard size={18} />
              {loading ? 'Processing...' : 'Subscribe Now'}
            </Button>

            <p className="text-center text-[#71717A] text-xs mt-4">
              Secure payment via Stripe. Cancel anytime.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-24">
          <h2 className="font-serif text-2xl text-[#F5F5F0] text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-[#F5F5F0] font-medium mb-2">Why do I need a membership?</h3>
              <p className="text-[#71717A] text-sm">
                The membership helps us maintain the platform, verify artists, and ensure 
                all artwork is authentic human creation.
              </p>
            </div>
            
            <div>
              <h3 className="text-[#F5F5F0] font-medium mb-2">Can I cancel anytime?</h3>
              <p className="text-[#71717A] text-sm">
                Yes! You can cancel your membership at any time. You'll keep access 
                until the end of your billing period.
              </p>
            </div>
            
            <div>
              <h3 className="text-[#F5F5F0] font-medium mb-2">What happens after I purchase art?</h3>
              <p className="text-[#71717A] text-sm">
                You get immediate access to the full-resolution file. The artwork 
                is removed from sale (unless it's a digital edition).
              </p>
            </div>
            
            <div>
              <h3 className="text-[#F5F5F0] font-medium mb-2">Are there any additional fees?</h3>
              <p className="text-[#71717A] text-sm">
                No hidden fees. You pay the listed price for artwork plus your 
                monthly membership. That's it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
